import React, { useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Link,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';

const ShareLinkModal = ({
  open,
  setOpen,
  shareUrl,
  title,
  copyValue,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  shareUrl: string;
  /** When set, this is shown and copied instead of shareUrl (e.g. for API curl). */
  copyValue?: string;
  /** Modal title. Defaults to "Share your Report". */
  title?: string;
}) => {
  const displayValue = copyValue ?? shareUrl;
  const modalTitle = title ?? 'Share your Report';

  const modalRoot = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'clamp(300px, 70vw, 900px)',
    bgcolor: 'background.paper',
    border: 'none',
    outline: 'none',
    boxShadow: 24,
    p: 4,
    borderRadius: '20px',
  };

  const [copySuccess, setCopySuccess] = useState('');

  const handleCopy = () => {
    navigator.clipboard.writeText(displayValue);
    setCopySuccess('Link copied!');
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="share-link-modal"
        aria-describedby="modal-to-share-link"
      >
        <Box sx={modalRoot}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="h2">
              {modalTitle}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            value={displayValue}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopy}>
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ my: 2 }}
          />

          {copySuccess && (
            <Typography color="success" variant="body2">
              <Alert severity="success">{copySuccess}</Alert>
            </Typography>
          )}

          {copyValue && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Automate this report with our API →{' '}
              <Link component={RouterLink} to="/api-dashboard" underline="hover">
                API Dashboard
              </Link>
            </Typography>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default ShareLinkModal;
